import { useRouteMatch } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { Button } from "@circle-react-shared/uikit/Button";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Modal } from "@circle-react-uikit/ModalV2";
import type { Appearance } from "../Store/interfaceSlice";
import type { CssVariables, SiteTheme } from "./constants";
import { useUpdateSiteTheme } from "./useUpdateSiteTheme";

interface SiteThemeConfirmationModalProps {
  appearance: Appearance;
  cssVariables: CssVariables;
  currentCanvasTheme: SiteTheme;
}

const SiteThemeConfirmationModalComponent = ({
  currentCanvasTheme,
  appearance,
  cssVariables,
}: SiteThemeConfirmationModalProps) => {
  const modal = useModal();

  const match = useRouteMatch<{ pageId: string }>(
    "/settings/pages/edit/:pageId",
  );
  const pageId = Number(match?.params.pageId);

  const { mutate: updateTheme, isLoading: isUpdatingTheme } =
    useUpdateSiteTheme();

  return (
    <Modal
      isOpen={modal.visible}
      onClose={() => {
        if (isUpdatingTheme) return;

        void modal.hide();
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title size="sm" className="font-bold">
            {t(
              "builder_ui.theme_configuration.update_site_theme_confirmation_modal.title",
            )}
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton
              onClick={modal.hide}
              disabled={isUpdatingTheme}
            />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="overflow-y-auto pb-11 md:!px-8">
          <Typography.BodyMd color="text-dark">
            {t(
              "builder_ui.theme_configuration.update_site_theme_confirmation_modal.description",
            )}
          </Typography.BodyMd>
        </Modal.Body>
        <Modal.Footer className="border-primary flex w-full justify-end gap-4 border-t">
          <Button
            variant="secondary"
            onClick={modal.hide}
            disabled={isUpdatingTheme}
          >
            {t(
              "builder_ui.theme_configuration.update_site_theme_confirmation_modal.secondary_cta",
            )}
          </Button>
          <Button
            variant="circle"
            onClick={() =>
              updateTheme(
                {
                  id: currentCanvasTheme.value,
                  theme_name: currentCanvasTheme.name,
                  appearance,
                  css_variables: cssVariables,
                  page_id: pageId,
                },
                {
                  onSuccess: () => {
                    void modal.hide();
                  },
                },
              )
            }
            disabled={isUpdatingTheme}
          >
            {t(
              "builder_ui.theme_configuration.update_site_theme_confirmation_modal.primary_cta",
            )}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const SiteThemeConfirmationModal = ModalManager.create(
  ({
    currentCanvasTheme,
    appearance,
    cssVariables,
  }: SiteThemeConfirmationModalProps) => (
    <SiteThemeConfirmationModalComponent
      appearance={appearance}
      cssVariables={cssVariables}
      currentCanvasTheme={currentCanvasTheme}
    />
  ),
);

export const useSiteThemeConfirmationModal = () =>
  useModal(SiteThemeConfirmationModal);
